import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { Lock, Mail, ArrowRight, Ship, Shield, CheckCircle } from "lucide-react";
import logo from "@/assets/velocity-logo-light.png";

type View = "login" | "forgot";

const CLIENT_PORTAL_ENTRY_KEY = "client-portal-entry";

const ClientLogin = () => {
  const [view, setView] = useState<View>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const resetForm = () => {
    setError("");
    setMessage("");
    setEmail("");
    setPassword("");
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error } = await signIn(email.trim(), password);
    if (error) {
      setError("Invalid email or password. Please try again.");
    } else {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (session?.user?.user_metadata?.must_change_password) {
        sessionStorage.setItem(CLIENT_PORTAL_ENTRY_KEY, "true");
        navigate("/change-password");
        setLoading(false);
        return;
      }
      if (userId) {
        const [{ data: isEmp }, { data: isAdmin }, { data: isAuditor }, { data: isClient }] = await Promise.all([
          supabase.rpc("has_role", { _user_id: userId, _role: "employee" }),
          supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
          supabase.rpc("has_role", { _user_id: userId, _role: "auditor" as any }),
          supabase.rpc("has_role", { _user_id: userId, _role: "client" }),
        ]);
        if (isAuditor) navigate("/auditor");
        else if (isClient) navigate("/portal");
        else if (isAdmin) navigate("/admin");
        else if (isEmp) navigate("/employee");
        else navigate("/portal");
      } else {
        navigate("/portal");
      }
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) setError(error.message);
    else setMessage("Password reset link sent to your email.");
    setLoading(false);
  };

  const title = view === "login" ? "Sign In" : "Reset Password";
  const subtitle = view === "login"
    ? "Secure access for authorized clients only"
    : "Enter your email to receive a reset link";

  return (
    <Layout>
      <div className="min-h-[80vh] flex items-center justify-center relative overflow-hidden gradient-navy">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-teal/8 to-transparent rounded-bl-full" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-lime/5 to-transparent rounded-tr-full" />

        <div className="relative z-10 w-full max-w-md mx-6 py-16">
          <div className="text-center mb-8">
            <a href="/"><img src={logo} alt="Velocity Offshore" className="h-14 mx-auto mb-6 opacity-90" /></a>
            <h1 className="font-display text-3xl text-primary-foreground tracking-tight mb-2">
              Client <span className="accent-text">Portal</span>
            </h1>
          </div>

          <div className="glass-card p-8 rounded-2xl">
            <p className="text-primary-foreground/40 text-sm text-center mb-6">{subtitle}</p>
            {message ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-teal mx-auto mb-4" />
                <p className="text-primary-foreground text-sm">{message}</p>
                <button onClick={() => { resetForm(); setView("login"); }} className="mt-4 text-teal text-sm font-bold hover:underline">
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <form onSubmit={view === "login" ? handleLogin : handleForgotPassword} className="space-y-5">
                  <div>
                    <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal/50" />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-11 pr-4 py-3.5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all" placeholder="your@company.com" required />
                    </div>
                  </div>

                  {view === "login" && (
                    <div>
                      <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal/50" />
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-11 pr-4 py-3.5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all" placeholder="••••••••" required />
                      </div>
                    </div>
                  )}

                  {view === "login" && (
                    <button type="button" onClick={() => { resetForm(); setView("forgot"); }} className="text-teal/70 hover:text-teal text-xs transition-colors">
                      Forgot your password?
                    </button>
                  )}

                  {error && <div className="px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">{error}</div>}

                  <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-xl hover:shadow-teal/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                    {loading ? "Please wait..." : title}
                    {!loading && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>

                {view === "forgot" && (
                  <button onClick={() => { resetForm(); setView("login"); }} className="mt-4 w-full text-center text-teal/70 hover:text-teal text-xs transition-colors">
                    Back to Sign In
                  </button>
                )}

                {view === "login" && (
                  <p className="text-center text-primary-foreground/25 text-xs mt-5">
                    Need an account? Contact your Velocity Offshore representative.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex justify-center gap-6 mt-8">
            <div className="flex items-center gap-2 text-primary-foreground/25 text-xs">
              <Shield className="w-3.5 h-3.5" /><span>256-bit Encrypted</span>
            </div>
            <div className="flex items-center gap-2 text-primary-foreground/25 text-xs">
              <Ship className="w-3.5 h-3.5" /><span>Velocity Offshore</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClientLogin;
